import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Modal from '@mui/material/Modal';

import * as Styled from '../LoyaltyPageSeparated.styles';

/* --- иконки --- */
const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/* --- типы --- */
export type FaqLine = string | { key: string; cls: string };

export interface FaqEntry {
  title: string;
  lines: FaqLine[];
}

export interface FaqGroup {
  label: string;
  items: FaqEntry[];
}

interface FaqTroubleshootModalProps {
  open: boolean;
  onClose: () => void;
  headerTitle: string;
  groups: FaqGroup[];
  ctaLabel?: string;
  onCtaClick?: () => void;
}

const BOLD = { b: <strong /> };
const faqKey = (suffix: string) => `LoyaltySeparated.Faq.${suffix}`;

const stripLeadingNumber = (title: string) => title.replace(/^\d+\.\s*/, '');

const FaqTroubleshootModal: React.FC<FaqTroubleshootModalProps> = ({
  open,
  onClose,
  headerTitle,
  groups,
  ctaLabel,
  onCtaClick,
}) => {
  const { t } = useTranslation();

  const flatItems = useMemo(
    () => groups.flatMap((group) => group.items),
    [groups]
  );

  const [selectedTitle, setSelectedTitle] = useState<string>(
    flatItems[0]?.title ?? ''
  );

  useEffect(() => {
    if (open) {
      setSelectedTitle(flatItems[0]?.title ?? '');
    }
  }, [open]);

  const selectedEntry = useMemo(
    () =>
      flatItems.find((item) => item.title === selectedTitle) ?? flatItems[0],
    [flatItems, selectedTitle]
  );

  const selectedHasClsLines = useMemo(
    () => !!selectedEntry?.lines.some((line) => typeof line !== 'string'),
    [selectedEntry]
  );

  let runningNumber = 0;

  return (
    <Modal open={open} onClose={onClose}>
      <Styled.ModalOverlay onClick={onClose}>
        <Styled.FaqModalCard onClick={(e) => e.stopPropagation()}>
          <Styled.FaqModalHeaderRow>
            <span className="faq-modal-icon">?</span>
            <span className="faq-modal-title">{headerTitle}</span>
            <Styled.CloseButton
              type="button"
              onClick={onClose}
              style={{ position: 'static', marginLeft: 'auto' }}
            >
              <CloseIcon />
            </Styled.CloseButton>
          </Styled.FaqModalHeaderRow>

          <Styled.FaqModalBody>
            <Styled.FaqSidebarColumn>
              {groups.map((group) => (
                <div key={group.label}>
                  <Styled.FaqGroupLabel>{group.label}</Styled.FaqGroupLabel>
                  {group.items.map((item) => {
                    runningNumber += 1;
                    const number = runningNumber;
                    const active = item.title === selectedEntry?.title;
                    return (
                      <Styled.FaqListItem
                        key={item.title}
                        type="button"
                        active={active}
                        onClick={() => setSelectedTitle(item.title)}
                      >
                        <span className="faq-item-number">{number}</span>
                        <span className="faq-item-text">
                          {stripLeadingNumber(t(faqKey(item.title)))}
                        </span>
                      </Styled.FaqListItem>
                    );
                  })}
                </div>
              ))}
            </Styled.FaqSidebarColumn>

            <Styled.FaqDetailColumn>
              {selectedEntry && (
                <>
                  <Styled.FaqDetailTitle>
                    {stripLeadingNumber(t(faqKey(selectedEntry.title)))}
                  </Styled.FaqDetailTitle>

                  {selectedHasClsLines ? (
                    <Styled.FaqPlainContent>
                      {selectedEntry.lines.map((line) => {
                        const key = typeof line === 'string' ? line : line.key;
                        const cls =
                          typeof line === 'string' ? undefined : line.cls;
                        return (
                          <span className={cls} key={key}>
                            <Trans i18nKey={faqKey(key)} components={BOLD} />
                          </span>
                        );
                      })}
                    </Styled.FaqPlainContent>
                  ) : (
                    <>
                      {selectedEntry.lines.length > 0 && (
                        <Styled.FaqHighlightBox>
                          <Trans
                            i18nKey={faqKey(
                              typeof selectedEntry.lines[0] === 'string'
                                ? selectedEntry.lines[0]
                                : selectedEntry.lines[0].key
                            )}
                            components={BOLD}
                          />
                        </Styled.FaqHighlightBox>
                      )}

                      {selectedEntry.lines.length > 1 && (
                        <>
                          <Styled.FaqStepsHeading>
                            {t('LoyaltySeparated.WHAT_TO_DO')}
                          </Styled.FaqStepsHeading>
                          <Styled.FaqStepsList>
                            {selectedEntry.lines.slice(1).map((line) => {
                              const key =
                                typeof line === 'string' ? line : line.key;
                              return (
                                <li key={key}>
                                  <Trans
                                    i18nKey={faqKey(key)}
                                    components={BOLD}
                                  />
                                </li>
                              );
                            })}
                          </Styled.FaqStepsList>
                        </>
                      )}
                    </>
                  )}

                  {ctaLabel && onCtaClick && (
                    <Styled.FaqCtaButton type="button" onClick={onCtaClick}>
                      {ctaLabel}
                    </Styled.FaqCtaButton>
                  )}
                </>
              )}
            </Styled.FaqDetailColumn>
          </Styled.FaqModalBody>
        </Styled.FaqModalCard>
      </Styled.ModalOverlay>
    </Modal>
  );
};

export default FaqTroubleshootModal;
